import { EnumerationsLogic } from "./EnumerationsLogic";
import { Avatar, AvatarPictureType, Calculation, LegalEntityType, PaymentChannel, PayrollDetails } from "../model";

/** Common logic for invoices */
export class InvoicesLogic {
  /**
   * Custom type guard that determines whether the payment object is a payroll (based on whether it calcs collection).
   * @param paymentObject Payment object that is either PayrollDetails or Calculation OR does not have calcs / employer properites that is used to determine the type.
   */
  public static isPayroll(paymentObject: Calculation | PayrollDetails | any): paymentObject is PayrollDetails {
    return paymentObject && (paymentObject as PayrollDetails).calcs !== undefined;
  }

  /**
   * Custom type guard that determines whether the payment object is a calculation (based on whether it employer property).
   * @param paymentObject Payment object that is either PayrollDetails or Calculation OR does not have calcs / employer properites that is used to determine the type.
   */
  public static isCalculation(paymentObject: Calculation | PayrollDetails | any): paymentObject is Calculation {
    return paymentObject && (paymentObject as Calculation).employer !== undefined;
  }

  /**
   * Gets and avatar image for a payment channel
   * Enum name is id, label as displayName, descr as description and sort order as 6-char string in sortableName.
   * Picture is either color-initials combination or pic as usual.
   * @param channel The channel to fetch avatar for.
   */
  public static getChannelAvatar(channel: PaymentChannel): Avatar {
    return this.getAllChannels().find((x) => x.id === channel);
  }

  /**
   * Gets all the supported payment channels defined in PaymentChannel as avatars.
   * Enum name is id, label as displayName, descr as description and sort order as 6-char string in sortableName.
   * Picture is either color-initials combination or pic as usual.
   */
  public static getAllChannels(): Avatar[] {
    if (!this.avatars) {
      this.avatars = EnumerationsLogic.getEnumMetadata("PaymentChannel").values
        .map((e) => {
          const picData = this.avatarPics[e.name] || this.avatarPics[PaymentChannel.Undefined];
          return {
            id: e.name,
            displayName: e.label,
            firstName: e.label,
            sortableName: ("000000" + e.order).slice(-6),
            description: e.descr,
            color: picData.color,
            entityType: picData.entityType || LegalEntityType.Company,
            initials: picData.initials,
            pictureType: picData.url === "default" ? AvatarPictureType.Uploaded : picData.pictureType,
            url: picData.url === "default" ? `https://cdn.salaxy.com/ng1/img/paymentmethod/${e.name}.png` : picData.url,
          };
        });
    }
    return this.avatars;
  }

  /**
   * Returns true if the channel is for customer funds account (cfa).
   * @param channel Any payment channel.
   * @returns True for customer funds account channels.
   */
  public static isCfa(channel: PaymentChannel): boolean {
    channel = channel ?? PaymentChannel.Undefined;
    switch(channel)
    {
        case PaymentChannel.Undefined:
        case PaymentChannel.PalkkausCfaFinvoice:
        case PaymentChannel.PalkkausEcfaFinvoice:
        case PaymentChannel.PalkkausCfaPaytrail:
        case PaymentChannel.PalkkausEcfaPaytrail:
        case PaymentChannel.PalkkausCfaReference:
        case PaymentChannel.PalkkausEcfaReference:
        case PaymentChannel.PalkkausCfaTest:
        case PaymentChannel.HolviCfa:
        case PaymentChannel.TalenomCfa:
            return true;
        default:
            return false;
    }
  }

  /**
   * Returns true if the channel is for customer funds account (ecfa).
   */
  public static isEcfa(channel: PaymentChannel): boolean {
    channel = channel ?? PaymentChannel.Undefined;
    switch(channel)
    {
        case PaymentChannel.PalkkausEcfaFinvoice:
        case PaymentChannel.PalkkausEcfaPaytrail:
        case PaymentChannel.PalkkausEcfaReference:
            return true;
        default:
            return false;
    }
  }

  private static avatars: Avatar[];

  private static avatarPics: { [key: string]: Avatar } = {

    [PaymentChannel.Undefined]: { color: "gray", initials: "?" },
    [PaymentChannel.Test]: { url: "default" },
    [PaymentChannel.ZeroPayment]: { url: "default" },
    [PaymentChannel.External]: { url: "default" },

    [PaymentChannel.PalkkausManual]: { url: "default" },
    [PaymentChannel.PalkkausWS]: { color: "#005f81", initials: "WS" },
    [PaymentChannel.PalkkausPersonal]: { url: "default",  },
    [PaymentChannel.PalkkausInvoices]: { url: "default",  },

    [PaymentChannel.PalkkausCfaPaytrail]: { url: "default" },
    [PaymentChannel.PalkkausCfaReference]: { url: "default" },
    [PaymentChannel.PalkkausCfaFinvoice]: { url: "default" },
    [PaymentChannel.PalkkausEcfaPaytrail]: { url: "default" },
    [PaymentChannel.PalkkausEcfaReference]: { url: "default" },
    [PaymentChannel.PalkkausEcfaFinvoice]: { url: "default" },
    [PaymentChannel.PalkkausCfaTest]: { url: "default" },

    [PaymentChannel.AccountorGo]: { url: "default" },
    [PaymentChannel.TalenomOnline]: { url: "default" },
    [PaymentChannel.TalenomCfa]: { url: "default" },
    [PaymentChannel.HolviCfa]: { url: "default" },
    [PaymentChannel.FinagoSolo]: { url: "default" },
    [PaymentChannel.Procountor]: { url: "default" },
    [PaymentChannel.Kevytyrittaja]: { url: "default" },
    [PaymentChannel.VismaNetvisor]: { url: "default" },
    [PaymentChannel.Askelma]: { url: "default" },
    [PaymentChannel.Briox]: { url: "default"},
    [PaymentChannel.Lemonaid]: { url: "default"},
    [PaymentChannel.Laskupiste]: { url: "default"},
    [PaymentChannel.PasseliMerit]: { url: "default"},
    [PaymentChannel.Odoo]: { url: "default"},
    [PaymentChannel.Ecom]: { url: "default"},
    [PaymentChannel.Kitsas]: { url: "default"},
    [PaymentChannel.Kompassi]: { url: "default"},
    [PaymentChannel.Domus]: { url: "default"},
    [PaymentChannel.Holvi]: { url: "default" },
  };
}
